/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import { useStaticQuery, graphql } from 'gatsby';

const generateKey = (pre) => {
  return `${pre}_${new Date().getTime()}`;
};

const SEO = ({
  canonicalUrl,
  description,
  image,
  lang,
  seoKeywords,
  schema,
  shouldIndex,
  title,
}) => {
  const location = useLocation();
  const {
    datoCmsSite: { faviconMetaTags, globalSeo, locale },
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery(graphql`
    query LayoutQuery {
      datoCmsSite {
        faviconMetaTags {
          ...GatsbyDatoCmsFaviconMetaTags
        }
        locale
        globalSeo {
          fallbackSeo {
            description
            image {
              url
            }
            title
            twitterCard
          }
          twitterAccount
        }
      }
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const keywords =
    seoKeywords &&
    seoKeywords.length > 0 &&
    seoKeywords.map((keyword) => keyword && keyword.keyword).join(', ');

  const seo = {
    title: title || globalSeo.fallbackSeo.title,
    description: description || globalSeo.fallbackSeo.description,
    image: (image && image.url) || globalSeo.fallbackSeo.image.url,
    url: `${siteUrl}${location.pathname}`,
    canonicalUrl: canonicalUrl || location.pathname,
    keywords,
  };

  return (
    <Helmet title={seo.title} htmlAttributes={{ lang: lang || locale }}>
      <meta name="title" content={seo.title} key="meta-title" />
      {seo.description && <meta name="description" content={seo.description} key="description" />}

      {seo.keywords && <meta name="keywords" content={seo.keywords} />}

      {faviconMetaTags &&
        faviconMetaTags.tags &&
        faviconMetaTags.tags.length > 0 &&
        faviconMetaTags.tags.map((tag) => (
          <link {...tag.attributes} key={generateKey(tag.attributes.href)} />
        ))}

      <meta name="image" content={seo.image} />

      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:type" content="website" />
      {seo.title && <meta property="og:title" content={seo.title} />}
      {seo.description && <meta property="og:description" content={seo.description} />}
      {seo.image && <meta property="og:image" content={seo.image} />}

      <meta property="twitter:card" content={globalSeo.fallbackSeo.twitterCard} />
      {globalSeo.twitterAccount && (
        <meta name="twitter:creator" content={globalSeo.twitterAccount} />
      )}
      {seo.title && <meta property="twitter:title" content={seo.title} />}
      {seo.description && <meta property="twitter:description" content={seo.description} />}
      {seo.image && <meta property="twitter:image" content={seo.image} />}
      {seo.image && <meta name="twitter:image" content={seo.image} />}

      {shouldIndex && <link rel="canonical" href={seo.canonicalUrl} key="canonical" />}
      {shouldIndex && <meta name="robots" content="all" key="robots" />}

      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }}
        />
      )}
    </Helmet>
  );
};

SEO.defaultProps = {
  lang: `en`,
  description: ``,
  shouldIndex: true,
};

SEO.propTypes = {
  canonicalUrl: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
  }),
  shouldIndex: PropTypes.bool,
  title: PropTypes.string,
};

export { SEO };
